import { Route, Routes } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';
import AnalyticsPage from '~/pages/Analytics';
import PractitionerDashboard from '~/pages/PractitionerDashboard';
import AppointmentsBoard from '~/pages/AppointmentsBoard';
import BloodTestManagement from '~/pages/BloodTestManagement';
import BloodTestPanelDetail from '~/pages/BloodTestPanelDetail';
import BookabilityCheck from '~/pages/BookabilityCheck';
import CMS from '~/pages/CMS';
import WaitlistSubmissions from '~/pages/WaitlistSubmissions';
import Logs from '~/pages/Logs';
import Permissions from '~/pages/Permissions';
import Materials from '~/pages/Materials';
import GiftCards from '~/pages/GiftCards';
import GiftCardTemplates from '~/pages/GiftCardTemplates';
import Products from '~/pages/Products';
import TreatmentNewVariant from '~/pages/TreatmentNewVariant';
import Treatments from '~/pages/Treatments';
import TreatmentsGOA from '~/pages/TreatmentsGOA';
import TreatmentVariant from '~/pages/TreatmentVariant';
import { useUserStore } from '~/stores/user-store';
import { UserRole } from '~/types/server-types/UserRole';
import useAuth from './auth/useAuth';
import Loading from './components/base/Loading';
import Layout from './components/layout/Layout';
import LoginLayout from './components/login/LoginLayout';
import AppointmentsCalendar from './pages/AppointmentsCalendar';
import AppointmentsCalendarNew from './pages/AppointmentsCalendarNew';
import Customer from './pages/Customer';
import Customers from './pages/Customers';
import Dashboard from './pages/Dashboard';
import BillingDraft from './pages/BillingDraft';
import BillingDrafts from './pages/BillingDrafts';
import CancellationFees from './pages/CancellationFees';
import Bundles from './pages/Bundles';
import Invoice from './pages/Invoice';
import Invoices from './pages/Invoices';
import Payment from './pages/Payment';
import Payments from './pages/Payments';
import Equipment from './pages/Equipment';
import EquipmentCategories from './pages/EquipmentCategory';
import Error404 from './pages/Error404';
import FeatureRequest from './pages/FeatureRequest';
import Login from './pages/Login';
import Practitioner from './pages/Practitioner';
import PractitionerCategories from './pages/PractitionerCategories';
import Practitioners from './pages/Practitioners';
import ProductCategories from './pages/ProductCategories';
import QM from './pages/QM';
import RoomCategories from './pages/RoomCategories';
import Rooms from './pages/Rooms';
import MedicationConfig from './pages/MedicationConfig';
import Store from './pages/Store';
import Stores from './pages/Stores';
import Treatment from './pages/Treatment';
import User from './pages/User';
import Users from './pages/Users';
import UserSettings from './pages/UserSettings';
import CashRegisters from './pages/CashRegisters';
import CashRegisterDetail from './pages/CashRegister';
import CashRegisterStatement from './pages/CashRegisterStatement';
import ShiftPlanning from './pages/ShiftPlanning';

const App = () => {
    const { authIsLoading, isAuthenticated, roles } = useAuth();

    const { role } = useUserStore(
        useShallow((state) => ({
            role: state.user?.role
        }))
    );

    if (authIsLoading) return <Loading />;
    if (isAuthenticated && !role) return <Loading />;

    return isAuthenticated ? (
        <Routes>
            {(role === UserRole.ADMIN || role === UserRole.OPERATOR) ? (
                <Route element={<Layout />}>
                    {/* <Route element={<Appointments />} path='appointments' />
                    <Route element={<AppointmentType />} path='appointment-types' />
                    <Route element={<Coupon />} path='coupon/:id' />
                    <Route element={<Coupons />} path='coupons' />
                    <Route element={<CreateCoupon />} path='createCoupon' />
                    <Route element={<CreateCustomer />} path='createCustomer' />
                    <Route element={<CreateProduct />} path='createProduct' />
                    <Route element={<CreateStore />} path='createStore' />
                    <Route element={<CreateUser />} path='createUser' />
                    <Route element={<Dev />} path='dev' />
                    <Route element={<MaterialCategories />} path='material-categories' />
                    <Route element={<Product />} path='product/:id' />
                    <Route element={<Products />} path='products' /> */}


                    <Route element={<AnalyticsPage />} path='analytics' />
                    <Route element={<BookabilityCheck />} path='bookability-check' />
                    <Route element={<PractitionerDashboard />} path='practitioner-dashboard' />
                    <Route element={<AppointmentsBoard />} path='appointments-board' />
                    <Route element={<ShiftPlanning />} path='shift-planning' />
                    <Route element={<AppointmentsCalendar />} path='appointments-calendar-old' />
                    <Route element={<AppointmentsCalendarNew />} path='appointments-calendar' />
                    <Route element={<BloodTestManagement />} path='blood-tests' />
                    <Route element={<BloodTestPanelDetail />} path='blood-tests/panel/:id' />
                    <Route element={<CMS />} path='cms/*' />
                    <Route element={<WaitlistSubmissions />} path='waitlist-submissions' />
                    <Route element={<Permissions />} path='permissions/*' />
                    <Route element={<Customer />} path='customer/:id' />
                    <Route element={<Customers />} path='customers' />
                    <Route element={<Dashboard />} path='/' />
                    <Route element={<BillingDraft />} path='billing-draft/:id' />
                    <Route element={<BillingDrafts />} path='billing-drafts' />
                    <Route element={<CancellationFees />} path='cancellation-fees' />
                    <Route element={<Invoice />} path='invoice/:id' />
                    <Route element={<Invoices />} path='invoices' />
                    <Route element={<Payment />} path='payment/:id' />
                    <Route element={<Payments />} path='payments' />
                    <Route element={<Bundles />} path='bundles' />
                    <Route element={<CashRegisters />} path='cash-registers' />
                    <Route element={<CashRegisterDetail />} path='cash-register/:id' />
                    <Route element={<CashRegisterStatement />} path='cash-register/:id/statement' />
                    <Route element={<Equipment />} path='equipment' />
                    <Route element={<EquipmentCategories />} path='equipment-categories' />
                    <Route element={<Error404 />} path='*' />
                    <Route element={<FeatureRequest />} path='feature-requests' />
                    <Route element={<GiftCards />} path='gift-cards' />
                    <Route element={<GiftCardTemplates />} path='gift-card-templates' />
                    <Route element={<Logs />} path='logs' />
                    <Route element={<Materials />} path='materials' />
                    <Route element={<Products />} path='products' />
                    <Route element={<Practitioner />} path='practitioner/:id' />
                    <Route element={<PractitionerCategories />} path='practitioner-categories' />
                    <Route element={<Practitioners />} path='practitioners' />
                    <Route element={<ProductCategories />} path='product-categories' />
                    <Route element={<QM />} path='qm' />
                    <Route element={<RoomCategories />} path='room-categories' />
                    <Route element={<Rooms />} path='rooms' />
                    <Route element={<MedicationConfig />} path='medication-config' />
                    <Route element={<Store />} path='store/:id' />
                    <Route element={<Stores />} path='stores' />
                    <Route element={<Treatment />} path='treatment/:id' />
                    <Route element={<TreatmentNewVariant />} path='treatment/:id/variants/new' />
                    <Route element={<TreatmentVariant />} path='treatment/:treatmentId/variants/:variantId' />
                    <Route element={<Treatments />} path='treatments' />
                    <Route element={<TreatmentsGOA />} path='treatments-goa' />
                    <Route element={<User />} path='user/:id' />
                    <Route element={<Users />} path='users' />
                    <Route element={<UserSettings />} path='userSettings' />
                </Route>
            ) : (
                <Route element={<Layout />}>
                    <Route element={<PractitionerDashboard />} path='practitioner-dashboard' />
                    <Route element={<AppointmentsBoard />} path='appointments-board' />
                    <Route element={<ShiftPlanning />} path='shift-planning' />
                    <Route element={<AppointmentsCalendar />} path='appointments-calendar-old' />
                    <Route element={<AppointmentsCalendarNew />} path='appointments-calendar' />
                    <Route element={<BloodTestManagement />} path='blood-tests' />
                    <Route element={<BloodTestPanelDetail />} path='blood-tests/panel/:id' />
                    <Route element={<WaitlistSubmissions />} path='waitlist-submissions' />
                    <Route element={<Customers />} path='customers' />
                    <Route element={<Customer />} path='customer/:id' />
                    <Route element={<Dashboard />} path='/' />
                    <Route element={<BillingDraft />} path='billing-draft/:id' />
                    <Route element={<BillingDrafts />} path='billing-drafts' />
                    <Route element={<CancellationFees />} path='cancellation-fees' />
                    <Route element={<Invoice />} path='invoice/:id' />
                    <Route element={<Invoices />} path='invoices' />
                    <Route element={<CashRegisters />} path='cash-registers' />
                    <Route element={<CashRegisterDetail />} path='cash-register/:id' />
                    <Route element={<CashRegisterStatement />} path='cash-register/:id/statement' />
                    <Route element={<Payment />} path='payment/:id' />
                    <Route element={<Payments />} path='payments' />
                    <Route element={<Bundles />} path='bundles' />
                    <Route element={<GiftCards />} path='gift-cards' />
                    <Route element={<Products />} path='products' />
                    <Route element={<Error404 />} path='*' />
                    <Route element={<Logs />} path='logs' />
                    <Route element={<FeatureRequest />} path='feature-requests' />
                    <Route element={<Practitioner />} path='practitioner/:id' />
                    <Route element={<Practitioners />} path='practitioners' />
                    <Route element={<UserSettings />} path='userSettings' />
                </Route>
            )}
        </Routes>
    ) : (
        <Routes>
            <Route element={<LoginLayout />}>
                <Route element={<Login />} path='' />
                <Route element={<Login />} path='*' />
            </Route>
        </Routes>
    );
};

export default App;
